import { ChangeEvent, useRef } from 'react';
import { FormField } from '../formFields/FormField';
import './fileUpload.styles.css'
 
type FileUploadProps = {
  label?: string;
  value: File | null;
  onChange: (file: File | null) => void;
  error?: string;
  accept?: string;
  maxSize?: number; // en MB
  className?: string;
};
 
export const FileUpload = ({
  label,
  value,
  onChange,
  error,
  accept = '.pdf,.doc,.docx',
  maxSize = 10, // 10MB por defecto
  className
}: FileUploadProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
 
  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
	const file = e.target.files?.[0] || null;
	 
	if (file) {
	  // Validar tamaño del archivo
	  if (file.size > maxSize * 1024 * 1024) {
		alert(`El archivo es demasiado grande. Tamaño máximo: ${maxSize}MB`);
		return;
	  }
	   
	  // Validar tipo de archivo
	  const allowedTypes = accept.split(',').map(type => type.trim());
	  const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
	   
	  if (!allowedTypes.includes(fileExtension)) {
		alert(`Tipo de archivo no permitido. Tipos permitidos: ${accept}`);
		return;
	  }
	}
	 
	onChange(file);
  };
 
  const handleClick = () => {
	fileInputRef.current?.click();
  };
 
  const handleRemove = () => {
	onChange(null);
	if (fileInputRef.current) {
	  fileInputRef.current.value = '';
	}
  };
 
  const formatFileSize = (bytes: number): string => {
	if (bytes === 0) return '0 Bytes';
	const k = 1024;
	const sizes = ['Bytes', 'KB', 'MB', 'GB'];
	const i = Math.floor(Math.log(bytes) / Math.log(k));
	return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };
 
  return (
	<FormField label={label} error={error} className={className}>
	  <div className="file-upload-container">
		<input
		  ref={fileInputRef}
		  type="file"
		  accept={accept}
		  onChange={handleFileChange}
		  style={{ display: 'none' }}
		/>
		 
		<div 
		  className="file-upload-display"
		  onClick={handleClick}
		  role="button"
		  tabIndex={0}
		  onKeyDown={(e) => {
			if (e.key === 'Enter' || e.key === ' ') {
			  e.preventDefault();
			  handleClick();
			}
		  }}
		>
		  {value ? (
			<div className="file-upload-selected">
			  <div className="file-info">
				<span className="file-name">{value.name}</span>
				<span className="file-size">({formatFileSize(value.size)})</span>
			  </div>
			  <button
				type="button"
				className="file-remove-btn"
				onClick={(e) => {
				  e.stopPropagation();
				  handleRemove();
				}}
				title="Eliminar archivo"
			  >
				×
			  </button>
			</div>
		  ) : (
			<div className="file-upload-placeholder">
			  <span className="file-upload-icon">📁</span>
			  <span className="file-upload-text">
				Haz clic para seleccionar archivo
			  </span>
			  <span className="file-upload-hint">
				{accept} (máx. {maxSize}MB)
			  </span>
			</div>
		  )}
		</div>
	  </div>
	</FormField>
  );
};